import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../chart';

export default function AreaGraphCharts({
  data,
  valueKey = 'value',
  dataKey,
  gradients = [],
  activeDots = [],
  graphsNames,
  MyTooltipComponent,
  tooltipComponent,
  minWidthNeeded,
  customDots = false,
  showPercentage = true
}) {
  const CustomYAxisTick = (props) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x + 20},${y + 5})`}>
        <text x={0} y={0} dx={-8} textAnchor="end" fill={'#94a3b8'} fontSize={12}>
          {`${payload[valueKey]}${showPercentage ? '%' : ''}`}
        </text>
      </g>
    );
  };

  const calculateMinimumWidth = () => {
    if (!dataKey || !data.length) return '100%';
    const maxTextLength = Math.max(...data.map(d => String(d[dataKey]).length));
    const baseWidth = Math.max(maxTextLength * 8, 60);
    return `max(100%, ${data.length * baseWidth}px)`;
  };

  return (
    <div style={{ position: 'relative', height: '100%', marginTop: '0.5rem', overflowX: 'scroll', maxWidth: '80vw', overflowY: 'hidden', paddingBottom: '0.75rem' }}>
      <div style={{ width: minWidthNeeded ? calculateMinimumWidth() : "100%", height: "100%" }}>
        <ChartContainer config={{}} className="h-full w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 5, right: 0, bottom: 15, left: -10 }}
            >
              <defs>
                {gradients.map((gradient, index) => (
                  <linearGradient
                    key={index}
                    id={`color${graphsNames[index]}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={gradient[0]} />
                    <stop offset="100%" stopColor={gradient[1]} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="#1e3163" strokeOpacity={0.6} />
              <XAxis
                dataKey={dataKey}
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={({ x, y, payload, index }) => {
                  const isCurrent = data[index]?.isCurrent;
                  const text = String(payload?.value || '');

                  return (
                    <g transform={`translate(${x},${y + 15})`}>
                      <text
                        x={0}
                        y={0}
                        textAnchor="middle"
                        fill={isCurrent ? '#007CF0' : '#94a3b8'}
                        fontSize={12}
                        style={{ wordBreak: 'keep-all' }}
                      >
                        {text}
                      </text>
                      {isCurrent && (
                        <circle cx={0} cy={5} r={3} fill={'#007CF0'} />
                      )}
                    </g>
                  );
                }}
                padding={{ left: 20, right: 20 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={<CustomYAxisTick />}
                width={40}
                interval={0}
              />
              {graphsNames.map((name, index) => (
                <Area
                  key={index}
                  type="linear"
                  dataKey={name}
                  stroke={gradients[index][0]}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={`url(#color${name})`}
                  dot={{ r: 4, fill: '#000', stroke: gradients[index][0], strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: activeDots[index], stroke: activeDots[index] }}
                />
              ))}
              <ChartTooltip
                content={tooltipComponent ? tooltipComponent : <ChartTooltipContent CustomTooltip={MyTooltipComponent} />}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
        {graphsNames.length > 1 && !customDots && (
          <div style={{ position: 'absolute', bottom: '0.75rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ height: 12, width: 12, backgroundColor: activeDots[1], borderRadius: '2px' }} />
              <span>24 hrs</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ height: 12, width: 12, backgroundColor: activeDots[0], borderRadius: '2px' }} />
              <span>48 hrs</span>
            </div>
          </div>
        )}
        {customDots && (
          <div style={{ position: 'absolute', bottom: '0.75rem', right: 0, display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ height: 8, width: 8, backgroundColor: activeDots[1], borderRadius: '2px' }} />
              <span style={{ textTransform: 'capitalize' }}>{graphsNames[1]}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ height: 8, width: 8, backgroundColor: activeDots[0], borderRadius: '2px' }} />
              <span style={{ textTransform: 'capitalize' }}>{graphsNames[0]}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
